import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import { unwrap, type ApiError } from "@/api/errors";
import { queryKeys } from "@/api/queryKeys";
import type {
  AdminOrg,
  AdminOrgCreate,
  AdminOrgDetail,
  AdminProject,
  AdminProjectCreate,
  AdminProjectPatch,
  AdminUser,
  AuthEvent,
  Flags,
  FlagsPatch,
  ProjectStatus,
  MemberInvite,
  MemberOut,
  PublishOut,
  ResponseNoteOut,
  ResponsesCompare,
  ValidateOut,
} from "@/api/types";

export type {
  AdminUser,
  AdminOrg,
  AdminOrgDetail,
  AdminOrgUser,
  AdminProject,
  AuthEvent,
  Flags,
  ResponsesCompare,
  ValidateOut,
  VersionDiff,
} from "@/api/types";

export function useAdminOrgs(q: string) {
  return useQuery<AdminOrg[], ApiError>({
    queryKey: queryKeys.admin.orgs(q),
    queryFn: async () => unwrap(await api.GET("/admin/orgs", { params: { query: q ? { q } : {} } })),
    placeholderData: (prev) => prev,
  });
}

export function useCreateOrg() {
  const qc = useQueryClient();
  return useMutation<AdminOrg, ApiError, AdminOrgCreate>({
    mutationFn: async (body) => unwrap(await api.POST("/admin/orgs", { body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "orgs"] }),
  });
}

export function useAdminOrg(id: string | undefined) {
  return useQuery<AdminOrgDetail, ApiError>({
    queryKey: queryKeys.admin.org(id ?? ""),
    enabled: Boolean(id),
    queryFn: async () => unwrap(await api.GET("/admin/orgs/{org_id}", { params: { path: { org_id: id ?? "" } } })),
  });
}

export function useCreateOrgUser(orgId: string) {
  const qc = useQueryClient();
  return useMutation<MemberOut, ApiError, MemberInvite>({
    mutationFn: async (body) => unwrap(await api.POST("/admin/orgs/{org_id}/users", { params: { path: { org_id: orgId } }, body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.admin.org(orgId) }),
  });
}

export function useCreateProject(orgId: string) {
  const qc = useQueryClient();
  return useMutation<AdminProject, ApiError, AdminProjectCreate>({
    mutationFn: async (body) => unwrap(await api.POST("/admin/orgs/{org_id}/projects", { params: { path: { org_id: orgId } }, body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.admin.org(orgId) }),
  });
}

export type UserAction = "resend-invite" | "deactivate" | "reset-password";

export function useUserAction(orgId: string) {
  const qc = useQueryClient();
  return useMutation<unknown, ApiError, { userId: string; action: UserAction }>({
    mutationFn: async ({ userId, action }) => {
      const params = { params: { path: { user_id: userId } } };
      switch (action) {
        case "resend-invite":
          return unwrap(await api.POST("/admin/users/{user_id}/resend-invite", params));
        case "deactivate":
          return unwrap(await api.POST("/admin/users/{user_id}/deactivate", params));
        case "reset-password":
          return unwrap(await api.POST("/admin/users/{user_id}/reset-password", params));
      }
    },
    onSuccess: (_d, v) => {
      if (v.action === "deactivate") void qc.invalidateQueries({ queryKey: queryKeys.admin.org(orgId) });
    },
  });
}

export function useAuthEvents(userId: string) {
  return useQuery<AuthEvent[], ApiError>({
    queryKey: queryKeys.admin.authEvents(userId),
    queryFn: async () =>
      unwrap(await api.GET("/admin/users/{user_id}/auth-events", { params: { path: { user_id: userId }, query: { limit: 100 } } })),
  });
}

export function useAdminProjects(filters: { status?: ProjectStatus; orgId?: string }) {
  return useQuery<AdminProject[], ApiError>({
    queryKey: queryKeys.admin.projects(filters.status ?? "", filters.orgId ?? ""),
    queryFn: async () =>
      unwrap(
        await api.GET("/admin/projects", {
          params: { query: { ...(filters.status ? { status: filters.status } : {}), ...(filters.orgId ? { org_id: filters.orgId } : {}) } },
        }),
      ),
    placeholderData: (prev) => prev,
  });
}

export function useAdminUsers(q: string) {
  return useQuery<AdminUser[], ApiError>({
    queryKey: queryKeys.admin.users(q),
    queryFn: async () => unwrap(await api.GET("/admin/users", { params: { query: q ? { q } : {} } })),
    placeholderData: (prev) => prev,
  });
}

export function useAdminProject(id: string) {
  return useQuery<AdminProject, ApiError>({
    queryKey: queryKeys.admin.project(id),
    queryFn: async () => unwrap(await api.GET("/admin/projects/{project_id}", { params: { path: { project_id: id } } })),
  });
}

export function usePatchProject(id: string) {
  const qc = useQueryClient();
  return useMutation<AdminProject, ApiError, AdminProjectPatch>({
    mutationFn: async (body) => unwrap(await api.PATCH("/admin/projects/{project_id}", { params: { path: { project_id: id } }, body })),
    onSuccess: (p) => {
      qc.setQueryData(queryKeys.admin.project(id), p);
      void qc.invalidateQueries({ queryKey: ["orgs"] });
      void qc.invalidateQueries({ queryKey: ["admin", "projects"] });
    },
  });
}

export function useRotateToken(id: string) {
  const qc = useQueryClient();
  return useMutation<{ feedback_token: string }, ApiError, void>({
    mutationFn: async () => unwrap(await api.POST("/admin/projects/{project_id}/feedback-token/rotate", { params: { path: { project_id: id } } })),
    onSuccess: ({ feedback_token }) =>
      qc.setQueryData<AdminProject>(queryKeys.admin.project(id), (old) => (old ? { ...old, feedback_token } : old)),
  });
}

/** Parsed questionnaire JSON; the API validates it against questionnaire.schema.json. */
export type QuestionnaireJson = Record<string, unknown>;

export function useValidateQuestionnaire(id: string) {
  return useMutation<ValidateOut, ApiError, QuestionnaireJson>({
    mutationFn: async (body) =>
      unwrap(await api.POST("/admin/projects/{project_id}/questionnaire/validate", { params: { path: { project_id: id } }, body: { body } })),
  });
}

export function usePublishQuestionnaire(id: string) {
  const qc = useQueryClient();
  return useMutation<PublishOut, ApiError, { body: QuestionnaireJson; notes: string; idempotencyKey: string }>({
    mutationFn: async ({ body, notes, idempotencyKey }) =>
      unwrap(
        await api.POST("/admin/projects/{project_id}/questionnaire/publish", {
          params: { path: { project_id: id }, header: { "idempotency-key": idempotencyKey } },
          body: { body, notes },
        }),
      ),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: queryKeys.admin.project(id) });
      await qc.invalidateQueries({ queryKey: ["orgs"] });
    },
  });
}

export function useResponsesCompare(id: string) {
  return useQuery<ResponsesCompare, ApiError>({
    queryKey: queryKeys.admin.responsesCompare(id),
    queryFn: async () =>
      unwrap(
        await api.GET("/admin/projects/{project_id}/responses/compare", { params: { path: { project_id: id } } }),
      ) as ResponsesCompare,
  });
}

/** Saves the current staff member's note on a question of `version` (the API keeps one per author). */
export function useSaveResponseNote(id: string, version: number) {
  const qc = useQueryClient();
  return useMutation<ResponseNoteOut, ApiError, { question_id: string; note: string }>({
    mutationFn: async ({ question_id, note }) =>
      unwrap(
        await api.POST("/admin/projects/{project_id}/response-notes", {
          params: { path: { project_id: id } },
          body: { template_version: version, question_id, note },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.admin.responsesCompare(id) }),
  });
}

export function useSettings() {
  return useQuery<Flags, ApiError>({
    queryKey: queryKeys.admin.settings,
    queryFn: async () => unwrap(await api.GET("/admin/settings")),
  });
}

export function usePatchSettings() {
  const qc = useQueryClient();
  return useMutation<Flags, ApiError, FlagsPatch>({
    mutationFn: async (body) => unwrap(await api.PATCH("/admin/settings", { body })),
    onSuccess: (flags) => qc.setQueryData(queryKeys.admin.settings, flags),
  });
}
